import { CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild, OnDestroy,
} from '@angular/core';
import { ReplaySubject, fromEvent, map, takeUntil } from 'rxjs';
import { Color } from 'src/app/models/color.models';

type TagsTemplateType = 'default' | 'yellowTemplate';

/** Tras leerse, una notificación de wellness sigue "activa" durante este lapso. */
const WELLNESS_ACTIVE_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-list-element',
  templateUrl: './list-element.component.html',
  styleUrls: ['./list-element.component.scss'],
  providers: [CdkDragHandle],
})
export class ListElementComponent implements OnDestroy {
  @ViewChild('checkContentInput')
  checkContentInput!: ElementRef<HTMLInputElement>;
  @Input() enableHover = true;
  @Input() templateTeams!: boolean;
  @Input() isDragable = false;
  @Input() withoutProgress!: boolean;
  @Input() isCheckable = false;
  @Input() mainColor!: string;
  @Input() cursorPointer = true;
  @Input() disabled = false;
  @Input() backgroundColor!: Color;
  /**
   * El flag wellnessWarningActive se recalcula al setear entityData, así el
   * template lee un booleano en vez de llamar a un método en cada change detection.
   */
  @Input()
  set entityData(value: any) {
    this._entityData = value;
    this.wellnessWarningActive = this.computeWellnessWarning(value);
  }
  get entityData(): any {
    return this._entityData;
  }
  private _entityData: any;
  public wellnessWarningActive = false;
  @Input() customTemplate: 'DEFAULT' | 'TEAMS' | 'USERS' | 'CLASSES' | 'CONTENTS' | 'ORGANIZATIONS' =
    'DEFAULT';
  @Input() tagsTemplate: TagsTemplateType = 'default';
  @Input() isSingleRowDesign = false;
  @Input() actions: { action: string; icon: string, icon_extra?: string, title?: string, enabled?: boolean; tooltip?: string }[] = [];
  @Input() selectable = false;
  @Input() selected = false;
  @Input() selectableDisabled = false;
  @Input() selectableTooltip = '';
  @Output() elementClick = new EventEmitter<HTMLInputElement>();
  @Output() actionClick = new EventEmitter<string>();
  @Output() selectionToggle = new EventEmitter<void>();
  private destroyed$ = new ReplaySubject<boolean>(1);
  public viewportWidth$ = fromEvent(window, 'resize').pipe(
    map((event: any) => event.target.innerWidth),
    takeUntil(this.destroyed$)
  );

  constructor() { }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public _onClick(event?: HTMLInputElement) {
    this.elementClick.emit(event);
  }

  public _onClickAction(event: Event, action: string): void {
    event.stopPropagation();
    this.actionClick.emit(action);
  }

  public _onSelectionClick(event: Event): void {
    event.stopPropagation();
    if (this.selectableDisabled) return;
    this.selectionToggle.emit();
  }

  public formatNumber(value: string): string {
    if (value.length > 1) {
      return value;
    }

    return '0' + value;
  }

  public getChaptersPercentage(completed: number, total: number): number {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }

  /**
   * Hay aviso de wellness si el alumno tiene al menos una notificación "activa":
   * sin leer (sin read_at) o leída hace menos de 3 días.
   */
  private computeWellnessWarning(entityData: any): boolean {
    const notifications: any[] = entityData?.notifications ?? [];
    return notifications.some((notification) =>
      this.isWellnessNotificationActive(notification)
    );
  }

  private isWellnessNotificationActive(notification: any): boolean {
    const readAt = notification?.read_at;

    // Sin leer (null / undefined / no viene) → siempre activa
    if (readAt === null || readAt === undefined || readAt === '') {
      return true;
    }

    const readDate = this.parseNotificationDate(readAt);
    if (!readDate) {
      return true;
    }

    return Date.now() - readDate.getTime() < WELLNESS_ACTIVE_WINDOW_MS;
  }

  private parseNotificationDate(value: string): Date | null {
    // Soporta 'DD-MM-YYYY' y formato ISO de datetime
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
      const [day, month, year] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
